import { useHandler } from "@suse-o11y/use-handler";
import { type Dispatch, type SetStateAction, useState } from "react";

import { isFunction } from "../is-function";

/**
 * A custom React hook that provides support for both controlled and uncontrolled state patterns {@link https://github.com/niekert/use-controlled-state inspired by the hook}.
 *
 * @template T - The type of the state value.
 * @param state - The current state value, used as the controlled value if `setState` is provided.
 * @param setState - Optional. The setter function for the controlled state. If not provided, the hook manages its own internal state.
 * @returns A tuple containing the current state value and a setter function. The setter updates both the internal state and the controlled state (if `setState` is provided).
 *
 * @remarks
 * - If `setState` is provided, the hook behaves as a controlled component, using the passed-in `state` and `setState`.
 * - If `setState` is not provided, the hook manages its own internal state.
 * - The setter function accepts either a value or an updater function, similar to React's `useState`.
 *
 * @example
 * // Uncontrolled usage:
 * const [value, setValue] = useControlledState(0);
 *
 * @example
 * // Controlled usage:
 * const [value, setValue] = useState(0);
 * const [controlledValue, setControlledValue] = useControlledState(value, setValue);
 */
export function useControlledState<T>(state: T, setState?: Dispatch<T>): [T, Dispatch<SetStateAction<T>>] {
	const [uncontrolledState, setUncontrolledState] = useState(state);

	const controlledState = setState == null ? uncontrolledState : state;

	const setControlledState = useHandler((setter: SetStateAction<T>) => {
		const resolvedValue = isFunction(setter) ? setter(controlledState) : setter;

		setUncontrolledState(resolvedValue);
		setState?.(resolvedValue);
	});

	return [controlledState, setControlledState];
}
